<script>
import { ref, onMounted, watch } from 'vue';
import vibevoxAiApi from 'dashboard/api/vibevoxAi';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useCaptain } from 'dashboard/composables/useCaptain';
import { useTrack } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import { CAPTAIN_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';
import AiTranslateControl from './AiTranslateControl.vue';

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    EditorModeToggle,
    CopilotMenuBar,
    AiTranslateControl,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
  },
  emits: ['setReplyMode', 'toggleEditorSize', 'executeCopilotAction'],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleModeToggle = () => {
      const newMode =
        props.mode === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(newMode);
    };

    const { captainTasksEnabled } = useCaptain();
    const showCopilotMenu = ref(false);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack(CAPTAIN_EVENTS.EDITOR_AI_MENU_OPENED, {
          conversationId: props.conversationId,
          entryPoint: 'top_panel',
        });
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: true,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: true,
      },
    };
    useKeyboardEvents(keyboardEvents);

    // VibeVox «Перехват ИИ»: тумблер вкл/выкл авто-ответа ИИ для этого диалога.
    // Кнопку показываем, только если диалог привязан к VibeVox-Telegram комнате (иначе getState != ok).
    const aiEnabled = ref(true);
    const showAiToggle = ref(false);
    const aiBusy = ref(false);

    const fetchAiState = async () => {
      if (!props.conversationId) {
        showAiToggle.value = false;
        return;
      }
      try {
        const { data } = await vibevoxAiApi.getState(props.conversationId);
        if (data && data.ok) {
          aiEnabled.value = data.enabled !== false;
          showAiToggle.value = true;
        } else {
          showAiToggle.value = false;
        }
      } catch (error) {
        showAiToggle.value = false;
      }
    };

    const toggleAi = async () => {
      if (aiBusy.value) return;
      aiBusy.value = true;
      try {
        const { data } = await vibevoxAiApi.setState(props.conversationId, !aiEnabled.value);
        if (data && data.ok) aiEnabled.value = data.enabled !== false;
      } catch (error) {
        // при ошибке оставляем прежнее состояние
      } finally {
        aiBusy.value = false;
      }
    };

    onMounted(fetchAiState);
    watch(() => props.conversationId, fetchAiState);

    return {
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
      captainTasksEnabled,
      handleCopilotAction,
      showCopilotMenu,
      toggleCopilotMenu,
      handleClickOutside,
      aiEnabled,
      showAiToggle,
      aiBusy,
      toggleAi,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-n-ruby-9' : 'text-n-slate-11';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
};
</script>

<template>
  <!-- VibeVox: на мобильном (< sm) панель переносит кнопки на 2-ю строку,
       чтобы тумблеры «ИИ»/«Перевод» не обрезались за краем экрана. -->
  <div
    class="flex flex-wrap sm:flex-nowrap justify-between gap-x-2 gap-y-1.5 py-2 sm:py-0 min-h-[3.25rem] sm:h-[3.25rem] items-center ltr:pl-3 ltr:pr-2 rtl:pr-3 rtl:pl-2"
  >
    <div class="flex flex-wrap sm:flex-nowrap items-center gap-1.5 sm:gap-2 min-w-0">
      <EditorModeToggle
        :mode="mode"
        :disabled="disabled"
        :is-reply-restricted="isReplyRestricted"
        @toggle-mode="handleModeToggle"
      />
      <!-- VibeVox «Перехват ИИ»: вкл/выкл авто-ответа ИИ. Дизайн — как сегмент «Ответить/Личная заметка»:
           серый скруглённый трек + белый «чип» на активном состоянии. Надпись «ИИ», без иконки. -->
      <button
        v-if="showAiToggle"
        type="button"
        :disabled="aiBusy"
        class="flex flex-shrink-0 items-center p-1 h-8 transition-all border rounded-full bg-n-alpha-2 active:scale-[0.995] active:duration-75 disabled:opacity-60 disabled:cursor-wait"
        :title="
          aiEnabled
            ? 'ИИ авто-отвечает клиенту. Нажмите, чтобы взять диалог на себя (ИИ замолчит).'
            : 'ИИ выключен — отвечаете вы. Нажмите, чтобы вернуть авто-ответы ИИ.'
        "
        @click="toggleAi"
      >
        <span
          class="flex items-center px-2 h-6 text-sm rounded-full transition-all duration-300 ease-in-out"
          :class="
            aiEnabled
              ? 'font-medium shadow-sm bg-n-solid-1 text-n-teal-11'
              : 'text-n-slate-11'
          "
        >
          {{ aiEnabled ? 'ИИ вкл' : 'ИИ выкл' }}
        </span>
      </button>
      <!-- VibeVox: двусторонний переводчик — тумблер «Перевод» + поиск-пикер 108 языков -->
      <AiTranslateControl
        :conversation-id="conversationId"
        :disabled="disabled"
        @change="$emit('translate-change', $event)"
      />
    </div>
    <div class="flex items-center mx-4 my-0">
      <div v-if="isMessageLengthReachingThreshold" class="text-xs">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <div v-if="captainTasksEnabled" class="flex items-center gap-2">
      <div class="relative">
        <NextButton
          ghost
          :disabled="disabled || isEditorDisabled"
          :class="{
            'text-n-violet-9 hover:enabled:!bg-n-violet-3': !showCopilotMenu,
            'text-n-violet-9 bg-n-violet-3': showCopilotMenu,
          }"
          sm
          icon="i-ph-sparkle-fill"
          @click="toggleCopilotMenu"
        />
        <CopilotMenuBar
          v-if="showCopilotMenu"
          v-on-click-outside="handleClickOutside"
          :has-selection="false"
          :editor-content="editorContent"
          :conversation-id="conversationId"
          class="ltr:right-0 rtl:left-0 bottom-full mb-2"
          @execute-copilot-action="handleCopilotAction"
        />
      </div>
      <NextButton
        ghost
        class="text-n-slate-11"
        sm
        icon="i-lucide-maximize-2"
        @click="$emit('toggleEditorSize')"
      />
    </div>
  </div>
</template>
